<template>
<div>
    <div class="bg-white shadow-o rounded-md sm:rounded-lg p-6 py-8 lg:px-8 md:mt-8">
        <h3 class="text-lg leading-6 text-gray-700 font-poppins font-bold">Let's Get Your Quote</h3>

        <span class="block text-sm leading-normal text-gray-500 mt-2" v-if="form?.cover_type?.value === COVER_TYPE.personalCover">
            Fill out a few of your details to get your personalised breakdown cover quote. <span class="font-bold" v-if="form?.people_form?.value > 1">Once you're happy with your price, you can add details of any additional members later.</span>
        </span>
        <span class="block text-sm leading-normal text-gray-500 mt-2" v-else>
            Fill out a few details so we can give you a personalised price for your {{ isBusinessFlow() ? 'business breakdown' : 'breakdown'}} cover.
        </span>
      
        <cover-details ref="coverDetails" :form="form" :input-small="true" :label-question="true" :white-label="whiteLabel"
                       :phoneNumber="phoneNumber"
                       :show-start-date-question="true"
                       :hide-lookup="true"
                       @remove-vehicle="removeVehicle"
                       @add-empty-vehicle="addEmptyVehicle()"
                        :dob-error="dobError"
                        :dob-age-error="dobAgeError"
                        :extra-vehicle-wording-for-older-vehicles="extraVehicleWordingForOlderVehicles"
                    ></cover-details>

        <email-input ref="emailInput" :form="form" :show-sub-wording="false" @focus="autoLookupRegistration"></email-input>

        <div v-if="emailRfcCheckFailed" class="mt-2 text-sm text-red-600">{{ emailRfcCheckFailed }}</div>
        <!--<div v-else class="mt-2 text-sm text-gray-500">We'll also <b>email</b> you a copy of <b>your quote</b></div>-->

        <phone-input ref="phoneInput" :form="form" @focus="autoLookupRegistration"></phone-input>

        <!-- Add Personal Wording -->
        <!--
        <div v-if="false && !this.isBusinessFlow() && flowIconVersion" class="flex items-center bg-gray-50 justify-center mt-8">
            <div class="text-xxs sm:text-sm font-base text-gray-700 font-poppins px-3 py-4 sm:p-4 sm:py-6">
                <span class="flex items-center">
                    <svg width="24" height="28" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 65.729 70.107" class="block lg:block flex-shrink-0 mr-3 sm:mr-2">
                        <g transform="translate(-16 0.005)">
                            <path
                                d="M77.689,11.453,51.4.5a6.593,6.593,0,0,0-5.053,0L20.053,11.453A6.573,6.573,0,0,0,16,17.523C16,44.7,31.679,63.491,46.331,69.6a6.593,6.593,0,0,0,5.053,0c11.735-4.89,30.345-21.775,30.345-52.075a6.573,6.573,0,0,0-4.04-6.07ZM71.226,27.092l-25.2,25.2a2.2,2.2,0,0,1-3.095,0L28.694,38.047a2.2,2.2,0,0,1,0-3.1l3.095-3.095a2.2,2.2,0,0,1,3.095,0l9.6,9.6L65.036,20.9a2.2,2.2,0,0,1,3.095,0L71.226,24a2.176,2.176,0,0,1,0,3.1Z"
                                class="shield-check-a"
                            />
                            <path
                                d="M147.22,154.853l-25.2,25.2a2.2,2.2,0,0,1-3.095,0l-14.241-14.241a2.2,2.2,0,0,1,0-3.1l3.095-3.095a2.2,2.2,0,0,1,3.095,0l9.6,9.6,20.554-20.554a2.2,2.2,0,0,1,3.095,0l3.095,3.095a2.176,2.176,0,0,1,0,3.1Z"
                                class="shield-check-b"
                                transform="translate(-75.994 -127.76)"
                            />
                        </g>
                    </svg>
                    <span class="text-sm">
                        Need Personal Cover? You can customise your quote in the next step
                    </span>
                </span>
            </div>
        </div>
        -->

    </div>
    <div class="col-span-6 flex justify-end mt-8">
        <button type="button"
                data-testid="get-quote"
                dusk="D-getYourQuoteButton"
                class="w-full flex items-center justify-center px-10 py-3 border text-base lg:text-lg font-bold font-poppins rounded-md"
                :class="{
                    'bg-gray-200 text-gray-400 text-opacity-100 cursor-pointer border-gray-300': !inputsValid,
                    'bg-green-500 hover:bg-green-700 cursor-pointer text-white border-transparent': inputsValid,
                    'text-lg lg:text-lg py-2.5 md:py-3': flowIconVersion
                }"
                @click="complete">
                <span v-if="(processingQuote || ctaSpinner) && inputsValid" class="flex items-center justify-center text-lg lg:text-lg">
                    <svg 
                        aria-hidden="true" 
                        role="status" 
                        class="inline w-4 h-4 mr-3 text-white animate-spin"
                        viewBox="0 0 100 101" 
                        fill="none" 
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                            fill="#E5E7EB"/>
                        <path
                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                            fill="currentColor"/>
                    </svg>
                    Getting your price...
                    <!-- The span below is used as a spacer to generate the height otherwise the button skrinks on processing -->
                    <span class="invisible inline-block pt-0.5 text-2xl w-0 md:hidden">&gt;</span>
                </span>
                <span v-else class="flex items-center justify-center text-lg lg:text-lg">
                    Get Your Quote <span v-if="flowIconVersion" class="ml-1.5 pt-0.5 md:pt-1 text-2xl lg:text-4xl md:hidden">&gt;</span>
                </span>
        </button>
        <!-- Button for future split test with animated icon -->
        <!-- <button type="button"
                    data-testid="get-quote"
                    dusk="D-getYourQuoteButton"
                    class="w-full flex items-center justify-center px-10 border text-base lg:text-lg font-bold font-poppins rounded-md py-3"
                    :class="{
                        'bg-gray-200 text-gray-400 text-opacity-100 cursor-pointer border-gray-300': !inputsValid,
                        'bg-green-500 hover:bg-green-700 cursor-pointer text-white border-transparent': inputsValid,
                        'custom-padding-icon-animation': flowIconVersion, 'py-3': !flowIconVersion
                    }"
                    @click="complete"
                    >
                    <span>Get Your Quote</span>
                    <span v-if="flowIconVersion" class="ml-1.5 pt-0.5 text-2xl lg:text-4xl cursor-pointer" :class="{
                        'animate-bounce-animation': inputsValid,
                        'rotateIcon': onHoverSubmitBtn
                    }">&gt;</span>
            </button> -->
        </div>
    </div>
</template>
<script>
import EmailInput from "../EmailInput";
import PhoneInput from "../PhoneInput";
import CoverDetails from "./CoverDetails";
import { COVER_TYPE } from "../../constants";
import { vehicleMotorhomeRegex, suggestCorrectedVRM, isValidVrm } from "../../common/vehicleMethods"
export default {
    name: "CaptureForm",
    components: {
        CoverDetails,
        EmailInput,
        PhoneInput,
    },
    props: {
        form: {
            type: Object,
            default: () => {},
        },
        whiteLabel: {
            type: Object || String,
            default: () => {},
        },
        phoneNumber: {
            type: String,
            default: '0330 113 2433'
        },
        emailRfcCheckFailed: {
            type: String,
            default: ''
        },
        flowIconVersion: {
            type: Boolean,
            default: false
        },
        ctaSpinner: {
            type: Boolean,
            default: false
        },
        extraVehicleWordingForOlderVehicles: {
          type: Boolean,
          default: false
        }
    },
    data() {
        return {
            blockFormSubmit: false,
            isButtonEnabled: false,
            emailValid: false,
            inputsValid: false,
            vehiclesValid: false,
            phoneInputted: false,
            firstNameInputted: false,
            surnameInputted: false,
            dobError: false,
            dobAgeError: false,
            isCompleteClicked: false,
            COVER_TYPE: COVER_TYPE,
            // onHoverSubmitBtn: false,
            processingQuote: false
        }
    },
    watch: {
        'form': {
            handler(newVal) {
                // Email validation
                this.emailValid = this.isValidEmail(newVal.emailAddress);
                // Phone validation
                this.phoneInputted = this.isPhoneValid(newVal.phone);

                // Vehicle validation
                this.vehiclesValid = this.areVehiclesValid(newVal.vehicles);
                this.allInputsStarted();

                if(this.emailRfcCheckFailed){
                    this.$emit('email-field-change', true)
                }


                // Personal cover specific validations
                if (this.form?.cover_type?.value === COVER_TYPE.personalCover) {
                    this.firstNameInputted = this.isFieldNotEmpty(newVal.name.value);
                    this.surnameInputted = this.isFieldNotEmpty(newVal.surname.value);
                    this.dobDayInputted = this.isFieldNotEmpty(newVal.dateOfBirth.day.value);
                    this.dobMonthInputted = this.isFieldNotEmpty(newVal.dateOfBirth.month.value);
                    this.dobYearInputted = this.isValidYear(newVal.dateOfBirth.year.value);
                }

                // DOB error check
                if (this.isCompleteClicked) {
                    this.dobError = !(this.dobDayInputted && this.dobMonthInputted && this.dobYearInputted);
                }

                this.allInputsStarted();
            },
            deep: true,
            immediate: true,
        },
    },
    methods: {
        isValidEmail(email) {
            return email && email.includes("@") && email.includes(".");
        },
        isPhoneValid(phone) {
            return phone && phone.value && phone.value?.length > 0;
        },
        areVehiclesValid(vehicles) {
            if (vehicles && vehicles[0] && vehicles[0].vrm && vehicles[0].vrm.value) {
                return vehicles.every(vehicle => {
                    const isExempt = vehicle?.result &&
                        vehicle?.result?.primaryError !== null || 
                        (!this.isBusinessFlow() && vehicle?.result?.nonBusinessError !== null);
                        
                  if(vehicle?.result?.primaryError === 'AboveMaxAge'){
                  return false;
                  }

                    if((vehicle?.result?.primaryError === 'AboveMaxAge') || (vehicle?.result?.otherVehicleError && vehicle?.result?.otherVehicleError?.length > 0)){
                        return false;
                    }


                    if (isExempt) {
                        return true;
                    }

                    return (
                        vehicle?.vrm?.value &&
                        vehicle?.year?.value &&
                        vehicle?.make?.value &&
                        vehicle?.model?.value
                    );
                });
            }
            return false;
        },
        isFieldNotEmpty(field) {
            return field && field.length > 0;
        },
        isValidYear(year) {
            return year && year.length === 4;
        },
        addEmptyVehicle() {
            this.$emit('add-empty-vehicle', true)
        },
        removeVehicle(key) {
            this.$emit('remove-vehicle', key)
        },
        allInputsStarted() {
            let validDate = false
            if(this.form && this.form.dateOfBirth && this.form.dateOfBirth.day?.value && this.form.dateOfBirth.month?.value && this.form.dateOfBirth.year?.value){
                validDate = this.dateValidator(this.form.dateOfBirth)
            }

            const personalInfoValid =
                this.firstNameInputted &&
                this.surnameInputted &&
                this.dobDayInputted &&
                this.dobMonthInputted &&
                this.dobYearInputted &&
                !this.dobError && validDate


            this.inputsValid =
                this.emailValid &&
                (this.vehiclesValid || personalInfoValid);

        },
        async autoLookupRegistration(){
            if (this.form && this.form.vehicles) {
                for (let key = 0; key < this.form.vehicles.length; key++) {
                    const vehicle = this.form.vehicles[key];
                    if (vehicle.vrm.value && vehicle.make.value === null && !isValidVrm(vehicle.vrm.value)) {
                        // check if vrm is 7 characters without space before suggesting
                        const cleanedVrm = vehicle.vrm.value.replace(/\s+/g, '').toUpperCase()
                        if(cleanedVrm.length == 7){
                            const {suggestion, hasChanged, validVrm } = suggestCorrectedVRM(vehicle.vrm.value)
                            if(hasChanged && validVrm) {
                                vehicle.vrm.value = suggestion
                                await this.lookupVrm(vehicle, key);
                            } else {
                                this.$set(vehicle.vrm, 'error', 'Please enter a valid vehicle registration');
                            } 
                        } else {
                            this.$set(vehicle.vrm, 'error', 'Please enter a valid vehicle registration');
                        }
                       
                    } else if(vehicle.vrm.value && vehicle.make.value === null && isValidVrm(vehicle.vrm.value)) {
                        await this.lookupVrm(vehicle, key);
                    }
                }
            }
        },
        async complete() {

            // Prevent double-clicks and multiple submissions
            if (this.processingQuote) {
                return;
            }
            this.processingQuote = true
            this.isCompleteClicked = true
            this.dobError = false
            this.dobAgeError = false
            this.isValidUserDetails = true

            if (!this.$refs.coverDetails.allVehiclesLookedUp) {
                await this.$refs.coverDetails.lookupAll()
                for (let i = 0; i < this.form.vehicles.length; i++) {
                    if (this.form.vehicles[i].result) {
                        if(this.form.vehicles[i].result.primaryError !== null || vehicleMotorhomeRegex(this.form.vehicles[i].result)){
                            this.blockFormSubmit = true
                        } else if (!this.isBusinessFlow() && this.form.vehicles[i].result.nonBusinessError){
                            this.blockFormSubmit = true
                        }
                    }
                }
                if (this.blockFormSubmit) {
                    document.getElementById("cover-details").scrollIntoView();
                    this.blockFormSubmit = false
                    this.processingQuote = false
                    return;
                }
            }

            let isValidUserDetails = true
            let coverVehiclesValidate = true

            if (this.form?.cover_type?.value === COVER_TYPE.personalCover) {
                isValidUserDetails =
                    await this.$refs.coverDetails.validateUserDetails();
            }

            if (this.form?.cover_type?.value !== COVER_TYPE.personalCover) {
                coverVehiclesValidate =
                    this.$refs.coverDetails.allVehiclesValid();
            }
            const coverValidates = await this.$refs.coverDetails.validates()
            const emailValidates = await this.$refs.emailInput.validates()
            const phoneValidates = await this.$refs.phoneInput.validates()
            if (
                !coverValidates ||
                !coverVehiclesValidate ||
                !emailValidates ||
                !phoneValidates ||
                !isValidUserDetails
            ) {
                document.getElementById("cover-details").scrollIntoView();
                this.processingQuote = false
                return;
            }

            this.$emit("complete-capture", true);
            this.processingQuote = false
        },
        allVehiclesLookedUp() {
            return undefined;
        },
    },
    mounted() {
        this.$nextTick(function () {
            this.$emit('load-trustpilot',true)
            this.$emit('send-hotjar-identifier', true)

            if (window.hj) {
              window.hj('stateChange', '/breakdown-cover/your-quote/get');  // Track page view
            }
        })
    },
};
</script>
<style scoped></style>
